import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface AuditLogEntry {
  id: string
  table_name: string
  record_id: string
  operation: 'INSERT' | 'UPDATE' | 'DELETE'
  changed_fields: string[] | null
  created_at: string
  user_email: string | null
  user_id: string | null
  old_data: Record<string, unknown> | null
  new_data: Record<string, unknown> | null
}

interface UseAuditLogOptions {
  tableName?: string
  recordId?: string
  userId?: string
  limit?: number
  enabled?: boolean
}

/**
 * Hook pour récupérer l'historique d'audit d'un enregistrement spécifique
 */
export function useRecordAuditHistory(tableName: string, recordId: string) {
  return useQuery({
    queryKey: ['audit-history', tableName, recordId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_record_history' as any, {
        p_table_name: tableName,
        p_record_id: recordId,
      })

      if (error) throw error
      return data as any as AuditLogEntry[]
    },
    enabled: !!tableName && !!recordId,
  })
}

/**
 * Hook pour récupérer les logs d'audit avec filtres
 */
export function useAuditLogs(options: UseAuditLogOptions = {}) {
  const { tableName, recordId, userId, limit = 100, enabled = true } = options

  return useQuery({
    queryKey: ['audit-logs', tableName, recordId, userId, limit],
    queryFn: async () => {
      let query = supabase
        .from('audit_log' as any)
        .select(
          `
          id,
          table_name,
          record_id,
          operation,
          changed_fields,
          created_at,
          user_email,
          user_id,
          old_data,
          new_data
        `
        )
        .order('created_at', { ascending: false })
        .limit(limit)

      if (tableName) {
        query = query.eq('table_name', tableName)
      }

      if (recordId) {
        query = query.eq('record_id', recordId)
      }

      if (userId) {
        query = query.eq('user_id', userId)
      }

      const { data, error } = await query

      if (error) throw error
      return data as any as AuditLogEntry[]
    },
    enabled,
  })
}

/**
 * Hook pour récupérer les modifications récentes de l'agence
 */
export function useRecentChanges(limit = 50) {
  return useQuery({
    queryKey: ['recent-changes', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recent_changes' as any)
        .select('*')
        .order('last_modified', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data
    },
  })
}

/**
 * Hook pour récupérer qui a créé et modifié un enregistrement
 */
export function useRecordMetadata(tableName: string, recordId: string) {
  return useQuery({
    queryKey: ['record-metadata', tableName, recordId],
    queryFn: async () => {
      // Récupérer la création
      const { data: creation, error: creationError } = await supabase
        .from('audit_log' as any)
        .select(
          `
          created_at,
          user_email,
          user_id
        `
        )
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .eq('operation', 'INSERT')
        .order('created_at', { ascending: true })
        .limit(1)
        .single()

      if (creationError && creationError.code !== 'PGRST116') {
        throw creationError
      }

      // Récupérer la dernière modification
      const { data: lastUpdate, error: updateError } = await supabase
        .from('audit_log' as any)
        .select(
          `
          created_at,
          user_email,
          user_id
        `
        )
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()

      if (updateError && updateError.code !== 'PGRST116') {
        throw updateError
      }

      return {
        created_at: (creation as any)?.created_at,
        created_by: (creation as any)?.user_email,
        created_by_id: (creation as any)?.user_id,
        updated_at: (lastUpdate as any)?.created_at,
        updated_by: (lastUpdate as any)?.user_email,
        updated_by_id: (lastUpdate as any)?.user_id,
      }
    },
    enabled: !!tableName && !!recordId,
  })
}

/**
 * Fonction utilitaire pour formater l'information de traçabilité
 */
export function formatAuditInfo(
  date?: string | null,
  userEmail?: string | null,
  userName?: string | null
): string {
  if (!date) return 'Inconnu'

  const dateStr = new Date(date).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  const user = userName || userEmail || 'Système'

  return `${dateStr} par ${user}`
}
